import React, { useEffect, useState, useRef, useMemo } from 'react';
import './DetectorStatus.css';
import FilterDropdown from "../TeamPortalUtil/FilterDropdown";
import TeamHeader from '../TeamHeader/TeamHeader';
import { FaMagnifyingGlass } from "react-icons/fa6";
import { useDetectorRelations } from './useDetectorRelations';

// Leaflet imports
import {
    MapContainer,
    TileLayer,
    Marker,
    Popup,
    useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { Icon, Map as LeafletMap } from "leaflet";

// Assets
import redMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-red.png'
import yellowMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-yellow.png'
import greenMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-blue.png'
import offlineMarker from '../../client-dashboard/client-dashboard-assets/mapdetectoricon-grey.png'
import googleMapsLogo from '../../backend/assets/google-map-logo.png'

// Toast imports
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Icons
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import { FaMapMarkerAlt } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import { BsExclamationSquare } from "react-icons/bs";
import { IoSearchSharp, IoClose } from "react-icons/io5";
import { MdFileDownload, MdDashboard, MdDeleteOutline, MdMyLocation, MdContentCopy, MdSensors, MdTune } from "react-icons/md";
import { PiPlugsFill } from "react-icons/pi";
import { AiOutlineAim } from "react-icons/ai";

interface FNNUsers {
    id: number;
    user_id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
}

// Interfaces for Most Recent Files
export interface DetectorFile {
    filename: string;
    timestamp: string;
    filepath: string;
}

export type AllServerFilesObject = {
    [siteName: string]: DetectorFile[];
};

export interface NewServerFilesFormat {
    data: AllServerFilesObject;
    last_triggered: string;
}

interface HardwareNote {
    note_id: number;
    maintenanced_by: number;
    note_related_detector: string;
    note_date_and_time: string;
    note_description: string;
    note_parameters: any;
    images?: { id: number; image: string }[];
    files?: { id: number; file: string }[];
}

export const redIcon = new Icon({
    iconUrl: redMarker,
    iconSize: [50, 50],
    iconAnchor: [25, 50],   // Bottom center of icon
    popupAnchor: [0, -45],  // Popup opens above marker tip
});

export const yellowIcon = new Icon({
    iconUrl: yellowMarker,
    iconSize: [50, 50],
    iconAnchor: [25, 50],
    popupAnchor: [0, -45],
});

export const BwIcon = new Icon({
    iconUrl: offlineMarker,
    iconSize: [50, 50],
    iconAnchor: [25, 50],
    popupAnchor: [0, -45],
});

export const greenIcon = new Icon({
    iconUrl: greenMarker,
    iconSize: [50, 50],
    iconAnchor: [25, 50],
    popupAnchor: [0, -45],
});

// Single source of truth for status colors (tiles, legend, health bar)
const STATUS_COLORS = {
    online: "#20bf55",
    warn: "#e0a100",
    critical: "#e5484d",
    offline: "#9aa6b2",
} as const;

const STALE_AFTER_MS = 30_000;

// ------------------------------------------------ Memory levels (per browser) ------------------------------------------------
export type MemoryThresholds = { warn: number; critical: number };

export const DEFAULT_MEMORY_THRESHOLDS: MemoryThresholds = { warn: 80, critical: 85 };
export const MEMORY_THRESHOLDS_KEY = 'detectorMemoryThresholds';

const isValidThresholds = (t: any): t is MemoryThresholds =>
    !!t &&
    Number.isInteger(t.warn) &&
    Number.isInteger(t.critical) &&
    t.warn >= 1 &&
    t.critical <= 100 &&
    t.warn < t.critical;

export const loadMemoryThresholds = (): MemoryThresholds => {
    try {
        const raw = localStorage.getItem(MEMORY_THRESHOLDS_KEY);
        if (!raw) return DEFAULT_MEMORY_THRESHOLDS;
        const parsed = JSON.parse(raw);
        return isValidThresholds(parsed)
            ? { warn: parsed.warn, critical: parsed.critical }
            : DEFAULT_MEMORY_THRESHOLDS;
    } catch {
        return DEFAULT_MEMORY_THRESHOLDS;
    }
};

const saveMemoryThresholds = (t: MemoryThresholds) => {
    try {
        localStorage.setItem(MEMORY_THRESHOLDS_KEY, JSON.stringify(t));
    } catch {
        // Storage can be blocked (private mode, quotas) — the values still apply for this session
    }
};

// "T" = no code or expired code, "U" = code entry without a code assigned
const getDisplayCode = (det: { code?: { expiration_date?: string; code?: string } }): string => {
    if (!det.code) return "T";
    const expiration = det.code.expiration_date ? new Date(det.code.expiration_date) : null;
    const notExpired = !expiration || expiration > new Date();
    return notExpired ? (det.code.code?.trim() || "U") : "T";
};

const toCodeId = (id: string | undefined) => {
    const n = parseInt(id ?? "", 10);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};

const formatTimeAgo = (date: Date | null, now: number) => {
    if (!date) return "—";
    const diff = Math.max(0, Math.floor((now - date.getTime()) / 1000));

    if (diff < 60) return `${diff} second${diff !== 1 ? 's' : ''} ago`;
    if (diff < 3600) {
        const m = Math.floor(diff / 60);
        return `${m} minute${m !== 1 ? 's' : ''} ago`;
    }
    if (diff < 86400) {
        const h = Math.floor(diff / 3600);
        return `${h} hour${h !== 1 ? 's' : ''} ago`;
    }
    const d = Math.floor(diff / 86400);
    return `${d} day${d !== 1 ? 's' : ''} ago`;
};

// Owns its own 1-second ticker so only this small element re-renders every second,
// not the whole page (map markers + every tile).
const LastUpdated = ({ lastFetched }: { lastFetched: Date | null }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    const isStale = !lastFetched || now - lastFetched.getTime() > STALE_AFTER_MS;

    return (
        <div className={`ds-live ${isStale ? 'is-stale' : ''}`} title={isStale ? 'No fresh data in the last 30 seconds' : 'Receiving live data'}>
            <span className="ds-live-dot" aria-hidden />
            <div className='detector-last-updated-text'>
                <p><strong>Last updated</strong></p>
                <p>{formatTimeAgo(lastFetched, now)}</p>
            </div>
        </div>
    );
};

// Leaflet measures its container once; tell it to re-measure after entering/leaving fullscreen
const MapResizer = ({ trigger }: { trigger: boolean }) => {
    const map = useMap();

    useEffect(() => {
        const id = setTimeout(() => {
            if (map && map.getContainer()) map.invalidateSize();
        }, 50);
        return () => clearTimeout(id);
    }, [trigger, map]);

    return null;
};

// Small popover to adjust the memory levels. Keeps its own draft state so typing
// doesn't re-render the whole page.
const MemorySettings = ({
    thresholds,
    onSave,
}: {
    thresholds: MemoryThresholds;
    onSave: (next: MemoryThresholds) => void;
}) => {
    const [open, setOpen] = useState(false);
    const [warn, setWarn] = useState(String(thresholds.warn));
    const [critical, setCritical] = useState(String(thresholds.critical));
    const [error, setError] = useState('');
    const ref = useRef<HTMLDivElement | null>(null);

    // Fresh draft every time the popover opens
    useEffect(() => {
        if (open) {
            setWarn(String(thresholds.warn));
            setCritical(String(thresholds.critical));
            setError('');
        }
    }, [open, thresholds]);

    // Close on outside click / Escape
    useEffect(() => {
        if (!open) return;

        const onPointerDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open]);

    const handleSave = () => {
        const next = { warn: Number(warn), critical: Number(critical) };

        if (!Number.isInteger(next.warn) || !Number.isInteger(next.critical)) {
            setError('Use whole numbers, e.g. 80 and 85.');
            return;
        }
        if (next.warn < 1 || next.critical > 100) {
            setError('Levels must be between 1 and 100%.');
            return;
        }
        if (next.warn >= next.critical) {
            setError('The warning level must be lower than the critical level.');
            return;
        }

        onSave(next);
        setOpen(false);
    };

    const handleReset = () => {
        onSave(DEFAULT_MEMORY_THRESHOLDS);
        setOpen(false);
    };

    const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') handleSave();
    };

    return (
        <div className="ds-settings" ref={ref}>
            <button
                type="button"
                className={`ds-icon-btn ds-settings-trigger ${open ? 'is-open' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={open}
                title="Adjust memory levels"
                onClick={() => setOpen(o => !o)}
            >
                <MdTune aria-hidden />
                <span className="ds-visually-hidden">Adjust memory levels</span>
            </button>

            {open && (
                <div className="ds-settings-pop" role="dialog" aria-label="Memory levels">
                    <p className="ds-settings-title">Memory levels</p>
                    <p className="ds-settings-hint">Tiles, markers and counts change color at these levels. Saved in this browser.</p>

                    <label className="ds-settings-row">
                        <span className="ds-dot" style={{ backgroundColor: STATUS_COLORS.warn }} />
                        <span className="ds-settings-label">Warning from</span>
                        <span className="ds-settings-input">
                            <input
                                type="number"
                                min={1}
                                max={99}
                                inputMode="numeric"
                                value={warn}
                                onChange={(e) => setWarn(e.target.value)}
                                onKeyDown={onEnter}
                                autoFocus
                            />
                            <span>%</span>
                        </span>
                    </label>

                    <label className="ds-settings-row">
                        <span className="ds-dot" style={{ backgroundColor: STATUS_COLORS.critical }} />
                        <span className="ds-settings-label">Critical from</span>
                        <span className="ds-settings-input">
                            <input
                                type="number"
                                min={2}
                                max={100}
                                inputMode="numeric"
                                value={critical}
                                onChange={(e) => setCritical(e.target.value)}
                                onKeyDown={onEnter}
                            />
                            <span>%</span>
                        </span>
                    </label>

                    {error && <p className="ds-settings-error" role="alert">{error}</p>}

                    <div className="ds-settings-actions">
                        <button type="button" className="ds-link" onClick={handleReset}>
                            Reset to {DEFAULT_MEMORY_THRESHOLDS.warn}% / {DEFAULT_MEMORY_THRESHOLDS.critical}%
                        </button>
                        <button type="button" className="ds-btn ds-btn-primary" onClick={handleSave}>
                            Save levels
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const DetectorStatus = () => {
    const { fullDetectors, detectorCodeObj, subAssemblies, detectors, refreshDetectors, lastFetched } = useDetectorRelations();

    const [regions, setRegions] = useState<string[]>(["All"]);
    const [selectedRegions, setSelectedRegions] = useState<string[]>(["All"]);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [testFilter, setTestFilter] = useState<"include" | "exclude">("include");
    const [selectedDetector, setSelectedDetector] = useState<typeof fullDetectors[0] | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("details");
    const [showFilePopup, setShowFilePopup] = useState(false);
    const [fnnUsers, setFnnUsers] = useState<FNNUsers[]>([]);
    const [hardwareNotes, setHardwareNotes] = useState<HardwareNote[]>([]);

    // Memory levels — read once from localStorage, defaults 80 / 85
    const [memThresholds, setMemThresholds] = useState<MemoryThresholds>(loadMemoryThresholds);

    const updateMemThresholds = (next: MemoryThresholds) => {
        setMemThresholds(next);
        saveMemoryThresholds(next);
        toast.success(`Memory levels saved: warning ${next.warn}%, critical ${next.critical}%`, { autoClose: 2000 });
    };

    // Keep other open tabs in sync when the levels change elsewhere
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === MEMORY_THRESHOLDS_KEY) setMemThresholds(loadMemoryThresholds());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Fetch FNN Users
    useEffect(() => {
        const fetchFNNUsers = async () => {
            try {
                const response = await fetch('/api/inventory/fnn-users/', {
                    credentials: "same-origin"
                });
                const data = await response.json();
                if (response.ok) {
                    setFnnUsers(data);
                } else {
                    console.error("Failed to load fnn users:", data.error || response.statusText);
                }
            } catch (error) {
                console.error("❌ Error fetching fnn users:", error);
            }
        };

        fetchFNNUsers();
    }, []);

    const fetchHardwareNotes = async () => {
        try {
            const response = await fetch('/api/hardware_history/', {
                credentials: "same-origin"
            });
            const data = await response.json();
            if (response.ok) {
                const parsedData = data.map((n: HardwareNote) => ({
                    ...n,
                    note_parameters: typeof n.note_parameters === 'string'
                        ? JSON.parse(n.note_parameters)
                        : n.note_parameters
                }));
                setHardwareNotes(parsedData);
            } else {
                console.error("Failed to fetch hardware notes:", data.error || response.statusText);
            }
        } catch (error) {
            console.error("❌ Error fetching hardware notes:", error);
        }
    };

    useEffect(() => {
        fetchHardwareNotes();
    }, []);

    // Extract unique regions from detector codes
    useEffect(() => {
        if (!detectorCodeObj.length) return;

        const unique = Array.from(
            new Set(detectorCodeObj.map(obj => obj.reg).filter(Boolean))
        ).filter(region => region !== "Test"); // Exclude "Test" region

        setRegions(["All", ...unique]);
    }, [detectorCodeObj]);

    // --- Filtered detectors (Region + Status + Search + Test) ---
    const filteredDetectors = useMemo(() => {
        let baseData = fullDetectors;

        // ----- REGION FILTER -----
        if (!selectedRegions.includes("All")) {
            baseData = baseData.filter(det => {
                if (!det.code) return false;
                return selectedRegions.includes(det.code.reg);
            });
        }

        // ----- SEARCH FILTER -----
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();

            baseData = baseData.filter(d => {
                const mac = d.monitoring.mac?.toLowerCase() || "";
                const site = d.monitoring.status?.site_name?.toLowerCase() || "";
                const ip = d.monitoring.ip?.toLowerCase() || "";
                const receiver = d.code?.receiver?.toLowerCase() || "";
                const codeName = d.code?.name?.toLowerCase() || "";
                const code = d.code?.code?.toLowerCase() || "";

                return (
                    mac.includes(term) ||
                    site.includes(term) ||
                    ip.includes(term) ||
                    receiver.includes(term) ||
                    codeName.includes(term) ||
                    code.includes(term)
                );
            });
        }

        // ----- STATUS FILTER -----
        if (statusFilters.length > 0) {
            baseData = baseData.filter(d => {
                const status = d.monitoring.online ? "Online" : "Offline";
                return statusFilters.includes(status);
            });
        }

        // ----- TEST FILTER -----
        if (testFilter === "exclude") {
            baseData = baseData.filter(d => getDisplayCode(d) !== "T");
        }

        return baseData;
    }, [fullDetectors, selectedRegions, searchTerm, statusFilters, testFilter]);

    // ---- Colors (all driven by the same memory levels) ----
    const getDetectorColor = (det: typeof fullDetectors[0]) => {
        if (!det.monitoring.online) return STATUS_COLORS.offline;
        const mem = parseFloat(det.monitoring.status.memory);
        if (mem >= memThresholds.critical) return STATUS_COLORS.critical;
        if (mem >= memThresholds.warn) return STATUS_COLORS.warn;
        return STATUS_COLORS.online;
    };

    const darkenColor = (hex: string, amount = 30): string => {
        let r = parseInt(hex.slice(1, 3), 16);
        let g = parseInt(hex.slice(3, 5), 16);
        let b = parseInt(hex.slice(5, 7), 16);

        r = Math.max(0, r - amount);
        g = Math.max(0, g - amount);
        b = Math.max(0, b - amount);

        const toHex = (n: number) => n.toString(16).padStart(2, "0");
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    };

    const getColorForMemory = (count: number) => {
        if (count >= memThresholds.critical) return STATUS_COLORS.critical;
        if (count >= memThresholds.warn) return "#b58300";
        return STATUS_COLORS.online;
    };

    const getColorForGPSFix = (isOn: boolean) => {
        if (isOn === false) return "red";
        if (isOn === true) return "#008000";
    };

    const getColorForMarker = (isOn: boolean, memoryValue: string | number) => {
        if (typeof memoryValue === "string" && /[a-zA-Z]/.test(memoryValue)) {
            return redIcon;
        }

        const numericMemory = Number(memoryValue);

        if (isOn === false) return BwIcon;
        if (Number.isNaN(numericMemory)) return redIcon;
        if (numericMemory >= memThresholds.critical) return redIcon;
        if (numericMemory >= memThresholds.warn) return yellowIcon;
        return greenIcon;
    };

    // Always read the freshest data for the selected detector (monitoring refreshes every 5s)
    const selected = selectedDetector
        ? fullDetectors.find(d => d.monitoring.mac === selectedDetector.monitoring.mac) ?? selectedDetector
        : null;

    const handleSelectDetector = (detector: typeof fullDetectors[0]) => {
        setSelectedDetector(detector);

        // Reset scope display when switching detectors
        setCommandResults(prev => ({ ...prev, SCOPE: '' }));

        if (activeTab === "config") {
            if (detector.monitoring.online) {
                if (detector.monitoring.ip) {
                    displayCommand(detector.monitoring.ip, "GET_LOC");
                    displayCommand(detector.monitoring.ip, "GET_DAC");
                    displayCommand(detector.monitoring.ip, "GET_THR");
                    displayCommand(detector.monitoring.ip, "GET_MEM");
                }
            } else {
                setActiveTab("details");
            }
        }
    };

    const getFnnUserName = (userId: string | number | null | undefined) => {
        if (!userId || !fnnUsers?.length) return "—";
        const user = fnnUsers.find((u) => String(u.id) === String(userId));
        if (!user) return "—";
        return `${user.first_name} ${user.last_name}`.trim() || user.username || "—";
    };

    // --------------------------------- Build hardware version info for selected detector -----------------------------------
    const detectorVersionInfo = (() => {
        if (!selected?.built) return null;

        const builtDet = selected.built;
        const detBom = selected.detectorBom;
        const builtSubs = selected.builtSubAssemblies || [];

        if (!detBom) return null;

        const subassemblyDisplay = builtDet.subassemblies.map(subId => {
            const builtSub = builtSubs.find(s => s.id === subId);
            let subName = "Unknown";

            if (builtSub) {
                const relatedSub = subAssemblies.find(sa => sa.id === builtSub.subassembly);
                if (relatedSub) subName = relatedSub.name;
            } else {
                const code = subId.split("-")[0];
                const relatedSub = subAssemblies.find(sa =>
                    sa.name.toLowerCase().includes(code.toLowerCase())
                );
                if (relatedSub) subName = relatedSub.name;
            }

            return `${subId}: ${subName}`;
        });

        return {
            built_at: builtDet.built_at,
            assembled_by: builtDet.assembled_by,
            subassemblies: subassemblyDisplay,
        };
    })();

    function showInfoTooltip(message: string, extraClassName?: string) {
        return (
            <div className="tooltip-wrapper">
                <FaCircleInfo />
                <div className={`custom-tooltip ${extraClassName ?? ""}`}>
                    {message}
                </div>
            </div>
        );
    }

    function Tooltip({
        message,
        showTooltip,
        children,
        extraClassName = "",
    }: {
        message: string;
        showTooltip: boolean;
        children: React.ReactNode;
        extraClassName?: string;
    }) {
        return (
            <div className="tooltip-button-wrapper">
                {children}
                {showTooltip && (
                    <div className={`tooltip-text show ${extraClassName}`}>
                        {message}
                    </div>
                )}
            </div>
        );
    }

    // ------------------------------------- Handles reference between dashboard and map -------------------------------------
    const mapRef = useRef<LeafletMap | null>(null);
    const markerRefs = useRef<{ [key: string]: any }>({});
    const openPopup = (mac: string) => {
        const marker = markerRefs.current[mac];
        if (marker) {
            marker.openPopup();
            const { lat, lng } = marker.getLatLng();
            const offsetLat = lat + 1;
            mapRef.current?.flyTo([offsetLat, lng], 7, { duration: 1.2 });
        }
    };

    const resetMapView = () => {
        mapRef.current?.closePopup();
        mapRef.current?.flyTo([0, 0], 2, { duration: 1.2 });
    };

    // ------------------------------------------------- Fullscreen map -------------------------------------------------
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        if (!isFullscreen) return;

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") setIsFullscreen(false);
        };
        const previousOverflow = document.body.style.overflow;

        document.addEventListener("keydown", onKeyDown);
        document.body.style.overflow = "hidden";

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [isFullscreen]);

    const clearSelection = () => {
        if (selected) markerRefs.current[selected.monitoring.mac]?.closePopup();
        setSelectedDetector(null);
        mapRef.current?.flyTo([0, 0], 2, { duration: 1.2 });
    };

    // ----------------------------------------- Handling pagination inside the grid -----------------------------------------
    const [currentPage, setCurrentPage] = useState(1);
    const [detectorsPerPage, setDetectorsPerPage] = useState(0);
    const gridRef = useRef<HTMLDivElement | null>(null);

    // 1️⃣ Preprocess and sort ALL detectors before pagination
    // Memoized + fully deterministic sort, so tiles keep their place between data refreshes
    const processedAndSortedDetectors = useMemo(() => filteredDetectors
        .map(det => {
            const displayCode = getDisplayCode(det);
            const displayName =
                det.code?.name?.trim() ||
                det.code?.code?.trim() ||
                det.monitoring.status.site_name;

            return { ...det, displayCode, displayName };
        })
        .sort((a, b) => {
            // "T" detectors go to the end
            if (a.displayCode === "T" && b.displayCode !== "T") return 1;
            if (a.displayCode !== "T" && b.displayCode === "T") return -1;

            // Then by code ID (missing IDs last)
            const idA = toCodeId(a.code?.id);
            const idB = toCodeId(b.code?.id);
            if (idA !== idB) return idA < idB ? -1 : 1;

            // Tie-breaker keeps the order stable (Infinity - Infinity used to give NaN)
            return a.monitoring.mac.localeCompare(b.monitoring.mac);
        }), [filteredDetectors]);

    // Fleet summary for the health bar (reflects current filters + memory levels)
    const fleet = processedAndSortedDetectors.reduce(
        (acc, d) => {
            const color = getDetectorColor(d);
            if (color === STATUS_COLORS.offline) acc.offline++;
            else if (color === STATUS_COLORS.critical) acc.critical++;
            else if (color === STATUS_COLORS.warn) acc.warn++;
            else acc.online++;
            return acc;
        },
        { online: 0, warn: 0, critical: 0, offline: 0 }
    );
    const fleetTotal = processedAndSortedDetectors.length;

    // 2️⃣ Pagination
    const totalPages = detectorsPerPage > 0 ? Math.max(1, Math.ceil(processedAndSortedDetectors.length / detectorsPerPage)) : 1;
    const startIndex = (currentPage - 1) * detectorsPerPage;
    const currentDetectors = processedAndSortedDetectors.slice(startIndex, startIndex + detectorsPerPage);

    // Columns depend only on the grid's width, so measure on resize only.
    // (Re-measuring whenever the tile count changed created a feedback loop:
    //  new count -> page height changes -> scrollbar toggles -> new column count -> ...)
    useEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        let frame = 0;
        const calculateDetectorsPerPage = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const columnCount = getComputedStyle(grid)
                    .getPropertyValue('grid-template-columns')
                    .trim()
                    .split(/\s+/)
                    .length;

                const perPage = Math.max(1, columnCount) * 4; // 4 rows
                setDetectorsPerPage(prev => (prev === perPage ? prev : perPage));
            });
        };

        calculateDetectorsPerPage();

        const observer = new ResizeObserver(calculateDetectorsPerPage);
        observer.observe(grid);

        return () => {
            observer.disconnect();
            cancelAnimationFrame(frame);
        };
    }, []);

    // Filters can shrink the page count; don't leave the user on an empty page
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };
    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    // -------------------------------------------------- Most Recent Files --------------------------------------------------
    const [fileDataUSA, setfileDataUSA] = useState<AllServerFilesObject | null>(null);
    const [fileDataLastModifiedUSA, setFileDataLastModifiedUSA] = useState<string | null>(null);
    const [fileSearchTerm, setFileSearchTerm] = useState('');

    const fetchFileData = async (trigger?: string, source?: any) => {
        try {
            const url = new URL(
                '/apidet/fnnbackend/fnnMostRecentFilesView',
                window.location.origin
            );

            if (trigger) {
                url.searchParams.append('trigger', trigger);
            }

            url.searchParams.append('origin', source);

            const response = await fetch(url.toString(), {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: "same-origin",
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data: NewServerFilesFormat = await response.json();

            if (source === 'dataUSA') {
                setfileDataUSA(data.data);
                setFileDataLastModifiedUSA(data.last_triggered);
            }

        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchFileData(undefined, 'dataUSA');

        const interval = setInterval(() => {
            fetchFileData(undefined, 'dataUSA');
        }, 50000);

        return () => clearInterval(interval);
    }, []);

    const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null);

    const handleFilePathSelected = (
        filepath: string,
        source: 'dataUSA'
    ) => {
        setSelectedFilePath(filepath);
        const encodedPath = encodeURIComponent(filepath);
        window.open(
            `/fnnbackend/plot?filepath=${encodedPath}&source=${source}`,
            '_blank'
        );
    };

    // ------------------------------------------- System Configuration -- FNN Tool -------------------------------------------
    const [commandResults, setCommandResults] = useState<{ [key: string]: string }>({});
    const [threshold, setThreshold] = useState('');
    const [memory, setMemory] = useState('');

    function displayNotification(successOrFail: boolean, message: string) {
        const options = {
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: {
                backgroundColor: '#ffffff',
                color: '#17232e',
                fontSize: '15px',
            }
        };

        if (successOrFail) toast.success(message, { ...options, autoClose: 4000 });
        else toast.error(message, { ...options, autoClose: 3000 });
    }

    const displayCommand = async (detectorIP: string, command: string, value = '', skipNotification = false) => {
        try {
            const url = new URL(`/apidet/fnnbackend/fnnToolView/`, window.location.origin);
            url.searchParams.append("ip", detectorIP);
            url.searchParams.append("command", command);
            url.searchParams.append("origin", 'dataUSA');

            if (command.startsWith("SET")) {
                url.searchParams.append("value", value);
            }

            const response = await fetch(url.toString(), {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                credentials: "same-origin",
            });

            if (!response.ok) throw new Error("Failed to fetch data");

            const data = await response.json();
            const formattedResult = data.result.includes(":") ? data.result.split(":")[1].trim() : data.result;

            setCommandResults(prev => ({ ...prev, [command]: formattedResult || "No data received" }));

            if (command === "GET_THR") setThreshold(formattedResult || '');
            if (command === "GET_MEM") setMemory(formattedResult || '');

        } catch (error) {
            console.error("Error fetching command:", error);
            displayNotification(false, "There was an issue completing the request.");
        }
    };

    const handleSetThreshold = (detectorIP: string) => {
        if (!/^\d+$/.test(threshold.trim())) {
            displayNotification(false, "Threshold must be a whole number between 5 and 2047.");
            return;
        }

        const thresholdValue = parseInt(threshold.trim(), 10);

        if (thresholdValue < 5 || thresholdValue > 2047) {
            displayNotification(false, "Threshold must be an integer between 5 and 2047.");
            return;
        }

        displayCommand(detectorIP, "SET_THR", threshold);
        setTimeout(() => displayCommand(detectorIP, "GET_THR", '', true), 500);
    };

    const handleSetMemory = (detectorIP: string) => {
        if (!/^\d+$/.test(memory.trim())) {
            displayNotification(false, "Memory must be a whole number between 1 and 99.");
            return;
        }

        const memoryValue = parseInt(memory.trim(), 10);

        if (memoryValue < 1 || memoryValue > 99) {
            displayNotification(false, "Memory must be an integer between 1 and 99.");
            return;
        }

        displayCommand(detectorIP, "SET_MEM", memory);
        setTimeout(() => displayCommand(detectorIP, "GET_MEM", '', true), 500);
    };

    function parseEncodedLocation(encoded: string) {
        if (!encoded) return null;

        const parts = encoded.split(",");
        if (parts.length !== 2) return null;

        const latRaw = parseInt(parts[0]);
        const lonRaw = parseInt(parts[1]);

        const latSign = (latRaw >> 31) & 0x1;
        const latDeg = (latRaw >> 23) & 0xFF;
        const latMin = (latRaw >> 17) & 0x3F;
        const latFrac = latRaw & 0x1FFFF;
        const lat = (latDeg + (latMin + latFrac / 131072) / 60) * (latSign ? 1 : -1);

        const lonSign = (lonRaw >> 31) & 0x1;
        const lonDeg = (lonRaw >> 23) & 0xFF;
        const lonMin = (lonRaw >> 17) & 0x3F;
        const lonFrac = lonRaw & 0x1FFFF;
        const lon = (lonDeg + (lonMin + lonFrac / 131072) / 60) * (lonSign ? -1 : 1);

        return {
            lat: parseFloat(lat.toFixed(5)),
            lon: parseFloat(lon.toFixed(5)),
        };
    }

    // Deleting the detector from detector.json
    const fetchCsrfToken = async () => {
        const response = await fetch('/api/csrf-token/', {
            method: 'GET',
            credentials: "same-origin",
        });

        if (response.ok) {
            const data = await response.json();
            return data.csrfToken;
        } else {
            console.error('Failed to fetch CSRF token');
            return null;
        }
    };

    const handleDeleteDetector = async () => {
        const csrfToken = await fetchCsrfToken();
        if (!csrfToken) {
            toast.error("Failed to get CSRF token");
            return;
        }

        if (!selected?.monitoring?.mac) {
            toast.error("No detector selected");
            return;
        }

        try {
            const response = await fetch(
                "/portal/api/detectors/delete/",
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken
                    },
                    credentials: "same-origin",
                    body: JSON.stringify({
                        mac: selected.monitoring.mac,
                    }),
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || data.error || data.detail || "Delete failed");
            }

            toast.success(`Detector ${selected.monitoring.mac} deleted`, {
                autoClose: 3000,
            });

            // Close the popup and zoom out the map
            const marker = markerRefs.current[selected.monitoring.mac];
            if (marker) {
                marker.closePopup();
            }
            mapRef.current?.flyTo([0, 0], 2, { duration: 1.2 });

            setSelectedDetector(null);
            setSearchTerm("");

            await refreshDetectors();

        } catch (err: any) {
            console.error("Delete detector error:", err);
            toast.error(err.message || "Failed to delete detector", {
                autoClose: 4000,
            });
        }
    };

    // Delete Popup
    const [showDeleteDetectorPopup, setShowDeleteDetectorPopup] = useState(false);

    // ------------------------------------------------------ UI helpers ------------------------------------------------------
    const copyToClipboard = async (value: string, label: string) => {
        try {
            await navigator.clipboard.writeText(value);
            toast.success(`${label} copied`, { autoClose: 1500 });
        } catch {
            toast.error(`Could not copy ${label}`);
        }
    };

    const kv = (label: string, value: React.ReactNode) => (
        <div className="ds-kv" key={label}>
            <span className="ds-kv-label">{label}</span>
            <span className="ds-kv-value">
                {value === undefined || value === null || value === '' ? '—' : value}
            </span>
        </div>
    );

    const pill = (text: string, tone: 'ok' | 'off' | 'warn' | 'crit') => (
        <span className={`ds-pill ds-pill-${tone}`}>
            <span className="ds-pill-dot" />
            {text}
        </span>
    );

    const algoOn = (v: string | boolean | undefined) => !!v && v == true;

    const copyable = (value: string | undefined, label: string) =>
        value ? (
            <button type="button" className="ds-copy" onClick={() => copyToClipboard(value, label)} title={`Copy ${label}`}>
                <span>{value}</span>
                <MdContentCopy aria-hidden />
            </button>
        ) : '—';

    const formatLastActiveUtc = (lastActive: string | undefined) =>
        lastActive
            ? new Date(lastActive + "Z")
                .toISOString()
                .replace("T", " ")
                .replace(/\.\d+Z$/, " UTC")
                .replace(/-/g, "/")
            : "N/A";

    // Hardware history link for the selected detector
    const detectorKey = selected?.code?.id && selected?.code?.code
        ? `${selected.code.id}_${selected.code.code}`
        : null;
    const hasHardwareNotes = !!detectorKey && hardwareNotes.some(n => n.note_related_detector === detectorKey);
    const openHardwareHistory = () => {
        if (detectorKey) window.open(`/hardware/history?search=${encodeURIComponent(detectorKey)}`, "_blank");
    };

    const openConfigTab = () => {
        if (selected?.monitoring.online && selected?.monitoring.ip) {
            setActiveTab("config");
            displayCommand(selected.monitoring.ip, "GET_LOC");
            displayCommand(selected.monitoring.ip, "GET_DAC");
            displayCommand(selected.monitoring.ip, "GET_THR");
            displayCommand(selected.monitoring.ip, "GET_MEM");
        }
    };

    const healthSegments = [
        { key: 'online', label: 'Online', count: fleet.online, color: STATUS_COLORS.online },
        { key: 'warn', label: `Memory ${memThresholds.warn}%+`, count: fleet.warn, color: STATUS_COLORS.warn },
        { key: 'critical', label: `Memory ${memThresholds.critical}%+`, count: fleet.critical, color: STATUS_COLORS.critical },
        { key: 'offline', label: 'Offline', count: fleet.offline, color: STATUS_COLORS.offline },
    ];

    return (
        <div className='customer-directory-body'>
            <ToastContainer />
            <TeamHeader title="Hardware" />

            <div className="ds-page">

                {/* Header */}
                <header className="ds-header">
                    <div>
                        <h1 className='cutomer-title ds-title'>Detector Management</h1>
                        <p className="ds-subtitle">Status, location and configuration for every detector in the network.</p>
                    </div>

                    <div className="ds-header-actions">
                        <LastUpdated lastFetched={lastFetched} />

                        <a href="https://matheus-vm.stegosaurus-forel.ts.net:8443/" target="_blank" rel="noreferrer"
                            className='ds-btn ds-btn-primary'
                        >
                            <MdDashboard aria-hidden />
                            <span>Dashboard</span>
                        </a>
                    </div>
                </header>

                {/* Control bar: search + filters on the left, fleet health on the right */}
                <section className="ds-controlbar">
                    <div className="ds-controlbar-tools">
                        <div className="customer-search-wrapper ds-search">
                            <FaMagnifyingGlass className="search-icon" />
                            <input
                                className="customer-search"
                                type="text"
                                name="Search_Field"
                                placeholder="Search by site, code, MAC or IP"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button type="button" className="ds-search-clear" onClick={() => setSearchTerm("")} aria-label="Clear search">
                                    <IoClose />
                                </button>
                            )}
                        </div>

                        <FilterDropdown
                            dropdownClassName="detector-status-dropdown"
                            sideClass="detector-side-drop"
                            initialSelected={{ test: [testFilter] }}
                            resetValue={{ test: ["include"] }}
                            sections={[
                                {
                                    key: "regions",
                                    title: "Region",
                                    options: regions.filter(r => r !== "All"),
                                    type: "checkbox",
                                },
                                {
                                    key: "status",
                                    title: "Status",
                                    options: ["Online", "Offline"],
                                    type: "checkbox",
                                },
                                {
                                    key: "test",
                                    title: "Test",
                                    options: ["include", "exclude"],
                                    type: "radio",
                                    formatLabel: (option) =>
                                        option.charAt(0).toUpperCase() + option.slice(1),
                                },
                            ]}
                            onChange={(filters) => {
                                setSelectedRegions(
                                    filters.regions && filters.regions.length > 0 ? filters.regions : ["All"]
                                );
                                setStatusFilters(filters.status || []);
                                setTestFilter(
                                    (filters.test && filters.test[0] ? filters.test[0] : "include") as
                                    | "include"
                                    | "exclude"
                                );
                            }}
                        />
                    </div>

                    <div className="ds-controlbar-health" aria-label="Fleet health">
                        <div className="ds-health-summary">
                            <span className="ds-health-total">
                                <strong>{fleetTotal}</strong> detector{fleetTotal !== 1 ? 's' : ''} in view
                            </span>

                            <div className="ds-health-legend">
                                {healthSegments.map(s => (
                                    <span key={s.key} className="ds-health-item">
                                        <span className="ds-dot" style={{ backgroundColor: s.color }} />
                                        <span className="ds-health-item-count">{s.count}</span>
                                        <span className="ds-health-item-label">{s.label}</span>
                                    </span>
                                ))}
                            </div>

                            <div className="ds-health-end">
                                <span className="ds-code-hint"><strong>U</strong> Unassigned {showInfoTooltip("Detectors whose code has not been assigned yet.")}</span>
                                <span className="ds-code-hint"><strong>T</strong> Test {showInfoTooltip("Detectors currently used for testing.")}</span>
                                <MemorySettings thresholds={memThresholds} onSave={updateMemThresholds} />
                            </div>
                        </div>

                        <div className="ds-health-bar" role="img"
                            aria-label={healthSegments.map(s => `${s.count} ${s.label}`).join(', ')}>
                            {fleetTotal > 0 ? healthSegments.filter(s => s.count > 0).map(s => (
                                <span
                                    key={s.key}
                                    className="ds-health-seg"
                                    style={{ flexGrow: s.count, backgroundColor: s.color }}
                                    title={`${s.label}: ${s.count}`}
                                />
                            )) : <span className="ds-health-seg ds-health-empty" />}
                        </div>
                    </div>
                </section>

                {/* Map + Detail panel */}
                <div className='ds-main'>
                    <div className={`ds-panel ds-map-panel hardware2-detec-info-wrapper-new-for-map${isFullscreen ? " ds-map-fullscreen" : ""}`}>
                        <MapContainer
                            center={[0.00, 0.00]}
                            zoom={2}
                            maxZoom={22}
                            style={{ width: "100%", height: "100%" }}
                            ref={mapRef}>

                            <MapResizer trigger={isFullscreen} />

                            <TileLayer
                                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; OpenStreetMap'
                                maxZoom={22}
                            />

                            {processedAndSortedDetectors.map((detector) => {
                                const lat = Number(detector.monitoring.status.gps_lat);
                                const lng = Number(detector.monitoring.status.gps_long);

                                if (!lat || !lng) return null;

                                return (
                                    <Marker
                                        key={detector.monitoring.mac}
                                        position={[lat, lng]}
                                        icon={getColorForMarker(detector.monitoring.online, Number(detector.monitoring.status.memory))}
                                        ref={el => (markerRefs.current[detector.monitoring.mac] = el)}
                                        eventHandlers={{
                                            click: () => handleSelectDetector(detector),
                                        }}
                                    >
                                        <Popup>
                                            <div className='new-popup-content ds-popup'>
                                                {detector.code?.code && <p style={{ paddingTop: '1rem' }}><strong>Code:</strong> {detector.code?.code}</p>}
                                                <p style={detector.code?.code ? undefined : { marginTop: "1rem" }}>
                                                    <strong>Detector:</strong>{" "}
                                                    <span style={{ color: '#166c91', fontWeight: 600 }}>
                                                        {detector.displayName || detector.monitoring.mac}
                                                    </span>
                                                </p>
                                                <p><strong>MAC:</strong> {detector.monitoring.mac}</p>
                                                <p>
                                                    <strong>Status:</strong>{" "}
                                                    <span style={{ color: detector.monitoring.online ? 'green' : 'red' }}>
                                                        {detector.monitoring.online ? 'Online' : 'Offline'}
                                                    </span>
                                                </p>
                                                <p><strong>Latitude:</strong> {lat}</p>
                                                <p><strong>Longitude:</strong> {lng}</p>
                                                <p className='pop-changeableValues'>
                                                    <strong> Memory: </strong>
                                                    <span style={{ color: getColorForMemory(Number(detector.monitoring.status.memory)) }}>
                                                        {detector.monitoring.status.memory}
                                                    </span>
                                                </p>
                                                <p className='pop-changeableValues'>
                                                    <strong> GPS Fix: </strong>
                                                    <span style={{ color: getColorForGPSFix(detector.monitoring.status.gps_fix) }}>
                                                        {detector.monitoring.status.gps_fix ? "Yes" : "No"}
                                                    </span>
                                                </p>
                                                <div className='google-maps-button-div'
                                                    onClick={() =>
                                                        window.open(
                                                            `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
                                                            "_blank",
                                                            "noopener,noreferrer"
                                                        )
                                                    }
                                                >
                                                    <img src={googleMapsLogo} alt='gmaps' />
                                                    <p>Google Maps</p>
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                );
                            })}
                        </MapContainer>

                        <div className="ds-map-actions">
                            <button type="button" className="ds-map-reset" onClick={resetMapView} title="Zoom out to all detectors">
                                <MdMyLocation aria-hidden />
                                <span>Reset view</span>
                            </button>

                            <button
                                type="button"
                                className="ds-map-fullscreen-btn"
                                onClick={() => setIsFullscreen(f => !f)}
                                aria-pressed={isFullscreen}
                                aria-label={isFullscreen ? "Exit fullscreen map" : "Show map fullscreen"}
                                title={isFullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
                            >
                                {isFullscreen ? (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M5 1H1v4M11 1h4v4M5 15H1v-4M11 15h4v-4M6 6l-5-5M10 6l5-5M6 10l-5 5M10 10l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M1 5V1h4M11 1h4v4M1 11v4h4M15 11v4h-4M1 1l5 5M15 1l-5 5M1 15l5-5M15 15l-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Detector Information */}
                    <div className='ds-panel ds-detail-panel'>
                        {selected ? (
                            <>
                                <div className="ds-detail-header">
                                    <div className="ds-detail-identity">
                                        <span
                                            className="ds-detail-badge"
                                            style={{ '--detector-color': getDetectorColor(selected) } as React.CSSProperties}
                                        >
                                            {getDisplayCode(selected)}
                                        </span>
                                        <div className="ds-detail-titles">
                                            <h2>{selected.code?.name || selected.monitoring.status?.site_name || 'Unknown site'}</h2>
                                            <div className="ds-detail-meta">
                                                <span>{selected.monitoring.mac}</span>
                                                {selected.monitoring.ip && <span>{selected.monitoring.ip}</span>}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="ds-detail-actions">
                                        {selected.monitoring.online ? pill('Online', 'ok') : pill('Offline', 'off')}

                                        {hasHardwareNotes && (
                                            <Tooltip
                                                message="This detector has hardware modifications. Click to see the history."
                                                showTooltip={true}
                                                extraClassName="tooltip-no-arrow-left"
                                            >
                                                <button type="button" className="ds-icon-btn ds-icon-btn-warn" onClick={openHardwareHistory} aria-label="Open hardware history">
                                                    <BsExclamationSquare />
                                                </button>
                                            </Tooltip>
                                        )}

                                        <button type="button" className="ds-icon-btn ds-icon-btn-danger" onClick={() => setShowDeleteDetectorPopup(true)} title="Delete detector" aria-label="Delete detector">
                                            <MdDeleteOutline />
                                        </button>
                                        <button type="button" className="ds-icon-btn" onClick={clearSelection} title="Close" aria-label="Close detector details">
                                            <IoClose />
                                        </button>
                                    </div>
                                </div>

                                <div className="ds-tabs" role="tablist">
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={activeTab === "details"}
                                        className={`ds-tab ${activeTab === "details" ? "active" : ""}`}
                                        onClick={() => setActiveTab("details")}
                                    >
                                        System details
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={activeTab === "hardware"}
                                        className={`ds-tab ${activeTab === "hardware" ? "active" : ""}`}
                                        onClick={() => setActiveTab("hardware")}
                                    >
                                        Hardware
                                    </button>
                                    <Tooltip
                                        message="Offline detectors cannot be configured"
                                        showTooltip={!selected.monitoring.online}
                                    >
                                        <button
                                            type="button"
                                            role="tab"
                                            aria-selected={activeTab === "config"}
                                            className={`ds-tab ${activeTab === "config" ? "active" : ""}`}
                                            disabled={!selected.monitoring.online}
                                            onClick={openConfigTab}
                                        >
                                            Configuration
                                        </button>
                                    </Tooltip>
                                </div>

                                <div className="ds-detail-body">
                                    {/* ---------------- System details ---------------- */}
                                    {activeTab === "details" && (() => {
                                        const st = selected.monitoring.status;
                                        return (
                                            <>
                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">Identity</h3>
                                                    <div className="ds-kv-grid">
                                                        {kv("Site", (
                                                            <button
                                                                type="button"
                                                                className="ds-link"
                                                                onClick={() =>
                                                                    window.open(
                                                                        `https://www.google.com/maps/search/?api=1&query=${st.gps_lat},${st.gps_long}`,
                                                                        "_blank",
                                                                        "noopener,noreferrer"
                                                                    )
                                                                }
                                                            >
                                                                {selected.code?.name || st.site_name}
                                                                <FaMapMarkerAlt aria-hidden />
                                                            </button>
                                                        ))}
                                                        {kv("Code", selected.code?.code || "N/A")}
                                                        {kv("MAC", copyable(selected.monitoring.mac, "MAC"))}
                                                        {kv("IP", copyable(selected.monitoring.ip, "IP"))}
                                                        {kv("SIM card", selected.code?.sim_card)}
                                                        {kv("Firmware", st.fw_version)}
                                                    </div>
                                                </div>

                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">Activity and health</h3>
                                                    <div className="ds-kv-grid">
                                                        {kv("Last active", formatLastActiveUtc(selected.monitoring.last_active))}
                                                        {kv("Up since", st.up_since)}
                                                        {kv("Memory usage", (
                                                            <span style={{ color: getColorForMemory(Number(st.memory)) }}>
                                                                {st.memory ? `${st.memory}%` : '—'}
                                                            </span>
                                                        ))}
                                                        {kv("Threshold", st.thr)}
                                                        {kv("Linux", st.linux ? 'Yes' : 'No')}
                                                        {kv("Test mode", st.test_mode ? 'Enabled' : 'Disabled')}
                                                        {kv("Online last 24 hrs", "TBD")}
                                                        {kv("Files transferred / hour", (
                                                            <button type="button" className="ds-link" onClick={() => setShowFilePopup(true)}>
                                                                TBD, view files
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">Location</h3>
                                                    <div className="ds-kv-grid">
                                                        {kv("GPS fix", st.gps_fix ? pill('Yes', 'ok') : pill('No', 'crit'))}
                                                        {kv("Latitude", st.gps_lat)}
                                                        {kv("Longitude", st.gps_long)}
                                                    </div>
                                                </div>

                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">Algorithms</h3>
                                                    <div className="ds-algo-row">
                                                        {[
                                                            { label: 'DACQ', value: st.dacq_algo },
                                                            { label: 'Peak', value: st.peak_algo },
                                                            { label: 'File transfer', value: st.file_algo },
                                                            { label: 'CSV transfer', value: st.csv_algo },
                                                        ].map(a => (
                                                            <div key={a.label} className="ds-algo">
                                                                <span>{a.label}</span>
                                                                {algoOn(a.value) ? pill('Online', 'ok') : pill('Offline', 'off')}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </>
                                        );
                                    })()}

                                    {/* ---------------- Hardware ---------------- */}
                                    {activeTab === "hardware" && (
                                        detectorVersionInfo ? (
                                            <>
                                                <div className="ds-section">
                                                    <div className="ds-section-head">
                                                        <h3 className="ds-section-title">Assembly</h3>
                                                        {detectorKey && (
                                                            <button type="button" className="ds-link" onClick={openHardwareHistory}>
                                                                View hardware history
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="ds-kv-grid">
                                                        {kv("Assembled on", new Date(detectorVersionInfo.built_at).toLocaleString())}
                                                        {kv("Assembled by", getFnnUserName(detectorVersionInfo.assembled_by))}
                                                        {kv("Detector version", (() => {
                                                            const detectorObj = detectors.find(
                                                                (d) => d.id === selected.built?.detector
                                                            );
                                                            return detectorObj ? detectorObj.version : "N/A";
                                                        })())}
                                                    </div>
                                                </div>

                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">
                                                        Sub-assemblies{" "}
                                                        {showInfoTooltip("Click a sub-assembly name to open its Bill of Materials (BOM).")}
                                                    </h3>
                                                    <div className="ds-sub-grid">
                                                        {detectorVersionInfo.subassemblies.map((s, i) => {
                                                            const [code, name] = s.split(':').map(str => str.trim());
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    key={i}
                                                                    className="ds-sub"
                                                                    onClick={() => {
                                                                        const url = `/inventory?openBOM=true&sub=${encodeURIComponent(name)}`;
                                                                        window.open(url, "_blank");
                                                                    }}
                                                                >
                                                                    <span className="ds-sub-code">{code}</span>
                                                                    <span className="ds-sub-name">{name}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>

                                                <div className="ds-button-row">
                                                    <button
                                                        type="button"
                                                        className="ds-btn ds-btn-ghost"
                                                        onClick={() => {
                                                            if (selected.built?.mac_address) {
                                                                const url = `/inventory?tab=units&mac=${encodeURIComponent(selected.built.mac_address)}&status=deployed`;
                                                                window.open(url, "_blank");
                                                            }
                                                        }}
                                                    >
                                                        View detector assembly
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="ds-btn ds-btn-ghost"
                                                        onClick={() => {
                                                            if (selected.built?.detector) {
                                                                const url = `/inventory?openBOM=true&det=${selected.built.detector}`;
                                                                window.open(url, "_blank");
                                                            }
                                                        }}
                                                    >
                                                        View detector BOM
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="ds-empty ds-empty-inline">
                                                <p>No hardware record is linked to this detector yet.</p>
                                                <span>Register the assembly in Inventory to see its build and sub-assemblies here.</span>
                                            </div>
                                        )
                                    )}

                                    {/* ---------------- Configuration ---------------- */}
                                    {activeTab === "config" && (
                                        selected.monitoring.online ? (
                                            <>
                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">Current values</h3>
                                                    <div className="ds-kv-grid">
                                                        {kv("Location", (() => {
                                                            const loc = parseEncodedLocation(commandResults["GET_LOC"]);
                                                            return loc ? (
                                                                <span className="ds-stack">
                                                                    <span>Lat {loc.lat.toFixed(5)}</span>
                                                                    <span>Lng {loc.lon.toFixed(5)}</span>
                                                                </span>
                                                            ) : (commandResults["GET_LOC"] || "—");
                                                        })())}
                                                        {kv("DAC", commandResults["GET_DAC"] ? (
                                                            <span className="ds-stack">
                                                                {commandResults["GET_DAC"].split(",").map((value, index) => (
                                                                    <span key={index}>{value.trim()}</span>
                                                                ))}
                                                            </span>
                                                        ) : "—")}
                                                        {kv("Threshold", commandResults["GET_THR"] || "—")}
                                                        {kv("Memory", commandResults["GET_MEM"] ? `${commandResults["GET_MEM"]}%` : "—")}
                                                    </div>
                                                </div>

                                                <div className="ds-section">
                                                    <h3 className="ds-section-title">Commands</h3>
                                                    <div className="ds-controls">
                                                        <div className="ds-control">
                                                            <span className="ds-control-label">Scope</span>
                                                            <button
                                                                type="button"
                                                                className="ds-btn ds-btn-primary"
                                                                onClick={() => {
                                                                    if (selected.monitoring.online && selected.monitoring.ip) {
                                                                        displayCommand(selected.monitoring.ip, "SCOPE");
                                                                    }
                                                                }}
                                                            >
                                                                <AiOutlineAim aria-hidden />
                                                                Run scope
                                                            </button>
                                                            <p className="ds-control-result">
                                                                {commandResults["SCOPE"] || "Run scope to read the current signal."}
                                                            </p>
                                                        </div>

                                                        <div className="ds-control">
                                                            <label className="ds-control-label" htmlFor="thr_inp">Threshold <span>5 to 2047</span></label>
                                                            <div className="ds-input-row">
                                                                <input
                                                                    id="thr_inp"
                                                                    type="text"
                                                                    name="thr_inp"
                                                                    inputMode="numeric"
                                                                    placeholder="e.g. 120"
                                                                    value={threshold}
                                                                    onChange={(e) => setThreshold(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && selected.monitoring.ip) {
                                                                            handleSetThreshold(selected.monitoring.ip);
                                                                        }
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="ds-btn ds-btn-ghost"
                                                                    onClick={() => selected.monitoring.ip && handleSetThreshold(selected.monitoring.ip)}
                                                                >
                                                                    Set
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <div className="ds-control">
                                                            <label className="ds-control-label" htmlFor="mem_inp">Memory limit <span>1 to 99%</span></label>
                                                            <div className="ds-input-row">
                                                                <input
                                                                    id="mem_inp"
                                                                    type="text"
                                                                    name="mem_inp"
                                                                    inputMode="numeric"
                                                                    placeholder="e.g. 80"
                                                                    value={memory}
                                                                    onChange={(e) => setMemory(e.target.value)}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter' && selected.monitoring.ip) {
                                                                            handleSetMemory(selected.monitoring.ip);
                                                                        }
                                                                    }}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="ds-btn ds-btn-ghost"
                                                                    onClick={() => selected.monitoring.ip && handleSetMemory(selected.monitoring.ip)}
                                                                >
                                                                    Set
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="ds-empty ds-empty-inline">
                                                <p>This detector is offline.</p>
                                                <span>Configuration is available again once it reconnects.</span>
                                            </div>
                                        )
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="ds-empty">
                                <MdSensors className="ds-empty-icon" aria-hidden />
                                <p>Select a detector</p>
                                <span>Click a tile below or a marker on the map to see its details.</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Detector Grid */}
                <section className="ds-grid-section">
                    <div className="ds-grid-head">
                        <h3>Detectors</h3>
                        <span className="ds-muted">
                            {fleetTotal > 0
                                ? `Showing ${startIndex + 1}–${Math.min(startIndex + detectorsPerPage, fleetTotal)} of ${fleetTotal}`
                                : 'No results'}
                        </span>
                    </div>

                    <div className='hardware2-detec-grid-wrapper'>
                        <div ref={gridRef} className='hardware2-detec-grid'>
                            {currentDetectors.length > 0 ? (
                                currentDetectors.map((det) => {
                                    const isSelected = selected?.monitoring.mac === det.monitoring.mac;
                                    const color = getDetectorColor(det);

                                    return (
                                        <button
                                            type="button"
                                            key={det.monitoring.mac}
                                            className={`hardware2-detec-square ${isSelected ? "selected" : selected ? "dimmed" : ""}`}
                                            style={{
                                                "--detector-color": color,
                                                "--detector-color-dark": darkenColor(color, 20),
                                            } as React.CSSProperties}
                                            title={`${det.displayName} | ${det.monitoring.ip} | Mem: ${det.monitoring.status.memory}`}
                                            aria-pressed={isSelected}
                                            onClick={() => {
                                                if (isSelected) {
                                                    clearSelection();
                                                } else {
                                                    handleSelectDetector(det);
                                                    openPopup(det.monitoring.mac);
                                                }
                                            }}
                                        >
                                            <span className="ds-sq-dot" aria-hidden />
                                            <span className="ds-sq-id">{det.code?.id ?? "-"}</span>
                                            <span className="ds-sq-code">{det.displayCode}</span>
                                        </button>
                                    );
                                })
                            ) : (
                                <div className="ds-grid-empty">
                                    <p>No detectors match your search or filters.</p>
                                    {searchTerm && (
                                        <button type="button" className="ds-link" onClick={() => setSearchTerm("")}>Clear search</button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Pagination */}
                    <nav className='ds-pagination' aria-label="Detector pages">
                        <button type="button" className="ds-page-btn" onClick={handlePrevPage} disabled={currentPage === 1} aria-label="Previous page">
                            <IoIosArrowBack />
                        </button>

                        {[...Array(totalPages)].map((_, i) => (
                            <button
                                type="button"
                                key={i}
                                className={`ds-page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                aria-current={currentPage === i + 1 ? 'page' : undefined}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </button>
                        ))}

                        <button type="button" className="ds-page-btn" onClick={handleNextPage} disabled={currentPage === totalPages} aria-label="Next page">
                            <IoIosArrowForward />
                        </button>
                    </nav>
                </section>

                {/* Files popup */}
                {showFilePopup && (
                    <div className='hardware2-popup-overlay' onClick={() => { setShowFilePopup(false); setFileSearchTerm(''); }}>
                        <div className='hardware2-popup-content' onClick={(e) => e.stopPropagation()}>
                            <div className='hardware2-file-title-and-search'>
                                <h1>{selected?.code?.code ? selected?.code?.code : selected?.code?.name} files</h1>

                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <div className="search-box">
                                        <IoSearchSharp className="hardware2-search-icon" />
                                        <input
                                            type="text"
                                            placeholder="Search files"
                                            className="hardware2-file-input"
                                            value={fileSearchTerm}
                                            onChange={(e) => setFileSearchTerm(e.target.value)}
                                        />
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => { setShowFilePopup(false); setFileSearchTerm(''); }}>
                                        <IoClose />
                                        Close
                                    </button>
                                </div>
                            </div>

                            <div className="hardware2-file-table-wrapper">
                                <table className='hardware2-file-strip'>
                                    <thead>
                                        <tr>
                                            <th>File name</th>
                                            <th>Timestamp</th>
                                            <th aria-label="Download"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            if (!fileDataUSA || !selected?.monitoring.status?.site_name) {
                                                return (
                                                    <tr>
                                                        <td colSpan={3} style={{ textAlign: 'center' }}>
                                                            No file data available.
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            const siteKey = Object.keys(fileDataUSA).find(site =>
                                                selected.code?.name?.toLowerCase().includes(site.toLowerCase())
                                            );

                                            let files = siteKey ? fileDataUSA[siteKey] : [];

                                            if (fileSearchTerm.trim() !== '') {
                                                const term = fileSearchTerm.toLowerCase();
                                                files = files.filter(file => file.filename.toLowerCase().includes(term));
                                            }

                                            if (!files?.length) {
                                                return (
                                                    <tr>
                                                        <td colSpan={3} style={{ textAlign: 'center' }}>
                                                            No recent files found for this site.
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            return files.map((file, index) => {
                                                const isBin = file.filename.toLowerCase().endsWith('.bin');

                                                return (
                                                    <tr
                                                        key={index}
                                                        style={{ cursor: isBin ? 'pointer' : 'not-allowed' }}
                                                        className={isBin ? 'clickable-file-row' : 'non-clickable-file-row'}
                                                    >
                                                        <td
                                                            style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}
                                                            onClick={isBin ? () => handleFilePathSelected(file.filepath, 'dataUSA') : undefined}
                                                        >
                                                            <strong>{index + 1}.</strong>
                                                            <span>{file.filename}</span>
                                                        </td>

                                                        <td
                                                            onClick={isBin ? () => handleFilePathSelected(file.filepath, 'dataUSA') : undefined}
                                                        >
                                                            {file.timestamp}
                                                        </td>

                                                        <td style={{ width: '3rem' }}>
                                                            <MdFileDownload
                                                                className='hardware2-download-icon'
                                                                title="Download file"
                                                                onClick={async (e) => {
                                                                    e.stopPropagation();

                                                                    try {
                                                                        const response = await fetch(
                                                                            `/apidet/fnnbackend/getFileToPlot?origin=dataUSA&path=${encodeURIComponent(file.filepath)}`,
                                                                            { method: 'GET', credentials: "same-origin" }
                                                                        );

                                                                        if (!response.ok) throw new Error(`HTTP error ${response.status}`);

                                                                        const blob = await response.blob();
                                                                        const url = window.URL.createObjectURL(blob);

                                                                        const a = document.createElement('a');
                                                                        a.href = url;
                                                                        a.download = file.filename;
                                                                        document.body.appendChild(a);
                                                                        a.click();
                                                                        a.remove();

                                                                        window.URL.revokeObjectURL(url);
                                                                    } catch (err) {
                                                                        console.error('Download failed:', err);
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Detector Popup */}
                {showDeleteDetectorPopup && selected && (
                    <div className="add-item-popup-overlay">
                        <div className="add-item-popup-content hh-del-pop">
                            <div className="hh-delete-inner-pop" style={{ gap: "2rem" }}>

                                <div className="hh-delete-text-and-icon">
                                    <PiPlugsFill className="hardware2-delete-icon" />

                                    <div>
                                        <h3>Delete detector</h3>

                                        <p>
                                            Are you sure you want to permanently delete this detector?
                                        </p>

                                        <p style={{ marginTop: "1rem" }}>
                                            <strong>MAC:</strong> {selected.monitoring.mac}<br />
                                            <strong>Site:</strong>{" "}
                                            {selected.code?.name ||
                                                selected.monitoring.status.site_name || 'Unknown site'}<br />
                                            <strong>IP:</strong> {selected.monitoring.ip}
                                        </p>

                                        <p style={{ marginTop: "1rem", color: "#d92d20" }}>
                                            This action cannot be undone.
                                        </p>
                                    </div>
                                </div>

                                <div className="hh-duo hh-actions">
                                    <button
                                        className="cancel-btn"
                                        onClick={() => setShowDeleteDetectorPopup(false)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="hh-delete-btn"
                                        onClick={async () => {
                                            setShowDeleteDetectorPopup(false);
                                            await handleDeleteDetector();
                                        }}
                                    >
                                        Delete detector
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DetectorStatus;